import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Divider,
  TextField,
  Paper,
  IconButton,
  useTheme,
  useMediaQuery,
  Alert,
  Chip,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  Close as CloseIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Event as EventIcon,
  Info as InfoIcon,
  Receipt as ReceiptIcon,
  Warning as WarningIcon,
  Autorenew as RenewalIcon
} from '@mui/icons-material';
import { format, differenceInDays } from 'date-fns';
import { it } from 'date-fns/locale';
import type { Scadenza } from '../../types/scadenza.types';
import StatusChip from '../common/StatusChip';

interface ConfirmScadenzaDialogProps {
  open: boolean;
  scadenza: Scadenza | null;
  onConfirm: (data: { numeroFattura: string; noteRinnovo?: string; nuovaDataScadenza?: string }) => void;
  onCancel: () => void;
  loading?: boolean;
}

interface FormData {
  numeroFattura: string;
  noteRinnovo: string;
  confermRinnovo: boolean;
  nuovaDataScadenza: Date | null;
}

interface FormErrors {
  numeroFattura?: string;
  noteRinnovo?: string;
  confermRinnovo?: string;
  nuovaDataScadenza?: string;
}

const ConfirmScadenzaDialog: React.FC<ConfirmScadenzaDialogProps> = ({
  open,
  scadenza,
  onConfirm,
  onCancel,
  loading = false
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  
  const [formData, setFormData] = useState<FormData>({
    numeroFattura: '',
    noteRinnovo: '',
    confermRinnovo: false,
    nuovaDataScadenza: null
  });
  
  const [errors, setErrors] = useState<FormErrors>({});

  // Reset form when dialog opens - pre-fill nuovaDataScadenza for rinnovabili
  React.useEffect(() => {
    if (open && scadenza) {
      const isRinn = scadenza.tipologia?.periodicitaRinnovoAnni &&
        scadenza.tipologia.periodicitaRinnovoAnni > 0;
      const now = new Date();
      const defaultDate = isRinn
        ? new Date(now.getFullYear() + scadenza.tipologia!.periodicitaRinnovoAnni!, now.getMonth(), now.getDate())
        : null;

      setFormData({
        numeroFattura: '',
        noteRinnovo: '',
        confermRinnovo: false,
        nuovaDataScadenza: defaultDate
      });
      setErrors({});
    }
  }, [open, scadenza]);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.numeroFattura.trim()) {
      newErrors.numeroFattura = 'Numero fattura è obbligatorio';
    } else if (formData.numeroFattura.length > 50) {
      newErrors.numeroFattura = 'Numero fattura non può superare i 50 caratteri';
    } else if (!/^[a-zA-Z0-9\s\-_\/]*$/.test(formData.numeroFattura)) {
      newErrors.numeroFattura = 'Numero fattura contiene caratteri non validi';
    }

    if (!formData.nuovaDataScadenza) {
      newErrors.nuovaDataScadenza = 'La nuova data di scadenza è obbligatoria';
    }

    if (formData.noteRinnovo.length > 500) {
      newErrors.noteRinnovo = 'Le note di rinnovo non possono superare i 500 caratteri';
    }

    // Se tipologia è rinnovabile, verifica conferma rinnovo
    const isRinnovabile = scadenza?.tipologia?.periodicitaRinnovoAnni && 
      scadenza.tipologia.periodicitaRinnovoAnni > 0;
    if (isRinnovabile && !formData.confermRinnovo) {
      newErrors.confermRinnovo = 'Devi confermare il rinnovo automatico';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleConfirm = () => {
    if (validateForm()) {
      // Converti la data in formato ISO se presente
      let nuovaDataScadenzaISO: string | undefined;
      if (formData.nuovaDataScadenza) {
        const date = formData.nuovaDataScadenza;
        const year = date.getFullYear();
        const month = date.getMonth();
        const day = date.getDate();
        // Usa mezzogiorno UTC per evitare shift di giorno
        const utcDate = new Date(Date.UTC(year, month, day, 12, 0, 0, 0));
        nuovaDataScadenzaISO = utcDate.toISOString();
      }

      onConfirm({
        numeroFattura: formData.numeroFattura.trim(),
        noteRinnovo: formData.noteRinnovo.trim() || undefined,
        nuovaDataScadenza: nuovaDataScadenzaISO
      });
    }
  };

  const handleFieldChange = (field: keyof FormData, value: string | boolean | Date | null) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear field error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  if (!scadenza) return null;

  // Calculate days of delay
  const today = new Date();
  const scadenzaDate = new Date(scadenza.dataScadenza);
  const daysDifference = differenceInDays(today, scadenzaDate);
  const isOverdue = daysDifference > 0;

  // Verifica se la tipologia è rinnovabile
  const isRinnovabile = scadenza.tipologia?.periodicitaRinnovoAnni && 
    scadenza.tipologia.periodicitaRinnovoAnni > 0;
  
  // Calcola nuova data di scadenza se rinnovabile
  const nuovaDataScadenza = isRinnovabile ? 
    new Date(today.getFullYear() + scadenza.tipologia!.periodicitaRinnovoAnni!, today.getMonth(), today.getDate()) :
    null;

  const titleId = 'confirm-scadenza-dialog-title';
  const descriptionId = 'confirm-scadenza-dialog-description';

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="md"
      fullWidth
      fullScreen={fullScreen}
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
      disableEscapeKeyDown={loading}
      PaperProps={{
        sx: { minHeight: fullScreen ? '100vh' : '80vh' }
      }}
    >
      <DialogTitle
        id={titleId}
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ReceiptIcon color="primary" />
          <Typography variant="h6" fontWeight={600}>
            Conferma Completamento Scadenza
          </Typography>
        </Box>
        <IconButton
          onClick={onCancel}
          disabled={loading}
          aria-label="Chiudi dialog"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ p: 3, pb: 2 }}>
          <Typography
            id={descriptionId}
            variant="body2"
            color="text.secondary"
            sx={{ mb: 3 }}
          >
            Stai per completare la scadenza. Verifica i dati e inserisci le informazioni richieste.
          </Typography>

          {/* Sezione 1: Dati Cliente */}
          <Paper
            variant="outlined"
            sx={{ mb: 3, overflow: 'hidden' }}
          >
            <Box sx={{ 
              bgcolor: 'primary.50', 
              px: 2, 
              py: 1.5,
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              {scadenza.cliente?.personaGiuridica ? (
                <BusinessIcon fontSize="small" color="primary" />
              ) : (
                <PersonIcon fontSize="small" color="primary" />
              )}
              <Typography variant="subtitle1" fontWeight={600} color="primary">
                Dati Cliente
              </Typography>
            </Box>
            <Box sx={{ p: 2 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {scadenza.cliente?.personaGiuridica ? 'Ragione Sociale' : 'Cognome e Nome'}
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {scadenza.cliente?.personaGiuridica
                      ? scadenza.cliente.nome
                      : `${scadenza.cliente?.cognome} ${scadenza.cliente?.nome}`.trim()}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {scadenza.cliente?.personaGiuridica ? 'P.IVA' : 'Codice Fiscale'}
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {scadenza.cliente?.personaGiuridica 
                      ? scadenza.cliente.partitaIva 
                      : scadenza.cliente?.codiceFiscale}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Email
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {scadenza.cliente?.email || 'Non disponibile'}
                  </Typography>
                </Box>
                {scadenza.cliente?.pec && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      PEC
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {scadenza.cliente.pec}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Paper>

          {/* Sezione 2: Dettagli Scadenza */}
          <Paper
            variant="outlined"
            sx={{ mb: 3, overflow: 'hidden' }}
          >
            <Box sx={{ 
              bgcolor: 'info.50', 
              px: 2, 
              py: 1.5,
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              <EventIcon fontSize="small" color="info" />
              <Typography variant="subtitle1" fontWeight={600} color="info.main">
                Dettagli Scadenza
              </Typography>
            </Box>
            <Box sx={{ p: 2 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Tipologia scadenza
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {scadenza.tipologia?.nome}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Data scadenza originale
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {format(new Date(scadenza.dataScadenza), 'dd MMMM yyyy', { locale: it })}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Stato attuale
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <StatusChip stato={scadenza.stato} size="small" />
                  </Box>
                </Box>
                {isOverdue && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Giorni di ritardo
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                      <Chip
                        icon={<WarningIcon />}
                        label={`${daysDifference} giorni`}
                        color={daysDifference > 30 ? 'error' : 'warning'}
                        size="small"
                      />
                    </Box>
                  </Box>
                )}
              </Box>
              {isOverdue && (
                <Alert
                  severity={daysDifference > 30 ? 'error' : 'warning'}
                  sx={{ mt: 2 }}
                  icon={<WarningIcon />}
                >
                  <Typography variant="body2">
                    {daysDifference > 30 
                      ? `Scadenza in ritardo di ${daysDifference} giorni. È necessaria particolare attenzione.`
                      : `Scadenza in ritardo di ${daysDifference} giorni.`}
                  </Typography>
                </Alert>
              )}
            </Box>
          </Paper>

          {/* Sezione 3: Gestione Rinnovo */}
          {isRinnovabile && (
            <Paper
              variant="outlined"
              sx={{ mb: 3, overflow: 'hidden' }}
            >
              <Box sx={{ 
                bgcolor: 'secondary.50', 
                px: 2, 
                py: 1.5,
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}>
                <RenewalIcon fontSize="small" color="secondary" />
                <Typography variant="subtitle1" fontWeight={600} color="secondary.main">
                  Gestione Rinnovo Automatico
                </Typography>
              </Box>
              <Box sx={{ p: 2 }}>
                <Alert
                  severity="info"
                  sx={{ mb: 2 }}
                  icon={<RenewalIcon />}
                >
                  <Typography variant="body2">
                    Questa tipologia di scadenza è rinnovabile ogni{' '}
                    <strong>{scadenza.tipologia!.periodicitaRinnovoAnni} anni</strong>.
                    Verrà automaticamente creata una nuova scadenza.
                  </Typography>
                </Alert>
                
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 2 }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Periodicità rinnovo
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {scadenza.tipologia!.periodicitaRinnovoAnni} anni
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Nuova data scadenza
                    </Typography>
                    <Typography variant="body1" fontWeight={500} color="primary">
                      {nuovaDataScadenza && format(nuovaDataScadenza, 'dd MMMM yyyy', { locale: it })}
                    </Typography>
                  </Box>
                </Box>
                
                {isOverdue && (
                  <Alert
                    severity={daysDifference > 30 ? 'error' : 'warning'}
                    sx={{ mb: 2 }}
                    icon={<WarningIcon />}
                  >
                    <Typography variant="body2">
                      <strong>Rinnovo in ritardo di {daysDifference} giorni</strong>
                      <br />
                      La nuova scadenza sarà calcolata dalla data odierna, 
                      ma verrà registrato il ritardo per la reportistica.
                    </Typography>
                  </Alert>
                )}
                
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.confermRinnovo}
                      onChange={(e) => handleFieldChange('confermRinnovo', e.target.checked)}
                      disabled={loading}
                      color="secondary"
                    />
                  }
                  label={
                    <Typography variant="body2">
                      Confermo il rinnovo automatico con la nuova data di scadenza
                    </Typography>
                  }
                  sx={{ mt: 1 }}
                />
                {errors.confermRinnovo && (
                  <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                    {errors.confermRinnovo}
                  </Typography>
                )}
              </Box>
            </Paper>
          )}

          {/* Sezione 4: Completamento */}
          <Paper
            variant="outlined"
            sx={{ overflow: 'hidden' }}
          >
            <Box sx={{ 
              bgcolor: 'success.50', 
              px: 2, 
              py: 1.5,
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              <InfoIcon fontSize="small" color="success" />
              <Typography variant="subtitle1" fontWeight={600} color="success.main">
                Completamento
              </Typography>
            </Box>
            <Box sx={{ p: 2 }}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Data completamento
                </Typography>
                <Typography variant="body1" fontWeight={500}>
                  {format(new Date(), 'dd MMMM yyyy', { locale: it })}
                </Typography>
              </Box>

              <TextField
                label="Numero fattura *"
                fullWidth
                value={formData.numeroFattura}
                onChange={(e) => handleFieldChange('numeroFattura', e.target.value)}
                error={!!errors.numeroFattura}
                helperText={errors.numeroFattura}
                disabled={loading}
                sx={{ mb: 2 }}
                inputProps={{
                  maxLength: 50,
                  'aria-describedby': 'numero-fattura-helper'
                }}
              />

              {/* Nuova Data Scadenza - obbligatoria */}
              <Box sx={{ mb: 2 }}>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={it}>
                  <DatePicker
                    label="Nuova Data Scadenza *"
                    value={formData.nuovaDataScadenza}
                    onChange={(newValue) => handleFieldChange('nuovaDataScadenza', newValue)}
                    disabled={loading}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!errors.nuovaDataScadenza,
                        helperText: errors.nuovaDataScadenza || 'Verrà creata una nuova scadenza identica con questa data'
                      }
                    }}
                    format="dd/MM/yyyy"
                    minDate={new Date()}
                  />
                </LocalizationProvider>
              </Box>

              <TextField
                label={isRinnovabile ? "Note per il rinnovo" : "Note aggiuntive"}
                multiline
                rows={3}
                fullWidth
                value={formData.noteRinnovo}
                onChange={(e) => handleFieldChange('noteRinnovo', e.target.value)}
                error={!!errors.noteRinnovo}
                helperText={errors.noteRinnovo || `${formData.noteRinnovo.length}/500 caratteri`}
                disabled={loading}
                inputProps={{
                  maxLength: 500
                }}
                placeholder={isRinnovabile ? 
                  "Es: Rinnovo effettuato con ritardo per motivi burocratici..." : 
                  "Note aggiuntive sul completamento..."
                }
              />
            </Box>
          </Paper>
        </Box>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 3 }}>
        <Button
          onClick={onCancel}
          disabled={loading}
          size="large"
        >
          Annulla
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="success"
          disabled={loading}
          size="large"
          autoFocus
        >
          {loading ? 'Completamento in corso...' : 'Conferma Completamento'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmScadenzaDialog;